import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  FileText,
  Building2,
  Loader2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import {
  usePartnerProjectDetail,
  usePartnerProjectUpdates,
} from '@/hooks/usePartnerProjects';
import { PartnerUpdatesFeed } from '@/components/partner/PartnerUpdatesFeed';
import { PartnerNoteForm } from '@/components/partner/PartnerNoteForm';

// Status colors matching portal status labels synced via portalSync
const statusColors: Record<string, string> = {
  // Portal workflow statuses (synced as partner-friendly labels)
  '1. Contract Needed': 'bg-red-100 text-red-800',
  '1.1 Contract Sent': 'bg-red-50 text-red-700',
  '1.2 Contract Signed': 'bg-orange-100 text-orange-800',
  '2. $$ Owed': 'bg-orange-100 text-orange-800',
  '3. Planning': 'bg-yellow-100 text-yellow-800',
  '3.1 Ready to Schedule': 'bg-yellow-100 text-yellow-800',
  '4. Scheduled': 'bg-lime-100 text-lime-800',
  '5. Job Started': 'bg-green-100 text-green-800',
  '6. Punch-List': 'bg-green-100 text-green-800',
  '7. Final Walk/COS': 'bg-emerald-100 text-emerald-800',
  '8. Reconciliation': 'bg-teal-100 text-teal-800',
  '9. Waiting For Final': 'bg-teal-100 text-teal-800',
  '10. Completed': 'bg-green-100 text-green-800',
  'Delayed': 'bg-gray-100 text-gray-800',
  'Job Not Sold': 'bg-gray-100 text-gray-600',
  'Warranty': 'bg-purple-100 text-purple-800',
  // Legacy statuses (for projects synced before this update)
  'New': 'bg-blue-100 text-blue-800',
  'In Progress': 'bg-yellow-100 text-yellow-800',
  'Working on it': 'bg-yellow-100 text-yellow-800',
  'Completed': 'bg-green-100 text-green-800',
  'Done': 'bg-green-100 text-green-800',
  'Closed': 'bg-green-100 text-green-800',
  'On Hold': 'bg-orange-100 text-orange-800',
  'Stuck': 'bg-red-100 text-red-800',
  'Cancelled': 'bg-gray-100 text-gray-800',
};

function getStatusColor(status: string): string {
  return statusColors[status] || 'bg-gray-100 text-gray-800';
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function getBusinessLabel(business: string): string {
  return business === 'azdisaster' ? 'ACR' : 'Blacksmith';
}

export function PartnerProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();

  const {
    data: project,
    isLoading: projectLoading,
    isError: projectError,
    error: projectErrorMessage,
  } = usePartnerProjectDetail(projectId || '');

  const {
    data: updates,
    isLoading: updatesLoading,
    refetch: refetchUpdates,
    isFetching: updatesFetching,
  } = usePartnerProjectUpdates(projectId || '');

  // Loading state
  if (projectLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
        <p className="text-gray-600 mt-4">Loading project...</p>
      </div>
    );
  }

  // Error state
  if (projectError || !project) {
    return (
      <div className="space-y-6">
        <Link
          to="/partner"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={18} />
          Back to Projects
        </Link>

        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="mx-auto text-red-500 mb-3" size={40} />
          <p className="text-red-800 font-medium">Failed to load project</p>
          <p className="text-red-600 text-sm mt-1">
            {projectErrorMessage instanceof Error
              ? projectErrorMessage.message
              : 'Project not found or access denied'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Link */}
      <Link
        to="/partner"
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft size={18} />
        Back to Projects
      </Link>

      {/* Project Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
            <div className="flex items-center gap-3 mt-2">
              <span
                className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(
                  project.status
                )}`}
              >
                {project.status}
              </span>
              {project.projectType && (
                <span className="text-sm text-gray-500">{project.projectType}</span>
              )}
            </div>
          </div>
        </div>

        {/* Project Details */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6 pt-6 border-t border-gray-200">
          {/* Business */}
          <div className="flex items-center gap-3">
            <div className="bg-gray-100 p-2 rounded-lg">
              <Building2 className="text-gray-600" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Business</p>
              <p className="font-medium text-gray-900">
                {getBusinessLabel(project.business)}
              </p>
            </div>
          </div>

          {/* Close Date */}
          <div className="flex items-center gap-3">
            <div className="bg-gray-100 p-2 rounded-lg">
              <Calendar className="text-gray-600" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Close Date</p>
              <p className="font-medium text-gray-900">
                {formatDate(project.closeDate)}
              </p>
            </div>
          </div>

          {/* Contract Signed */}
          <div className="flex items-center gap-3">
            <div className="bg-gray-100 p-2 rounded-lg">
              <FileText className="text-gray-600" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Contract Signed</p>
              <p className="font-medium text-gray-900">
                {formatDate(project.contractSignedDate)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Two Column Layout for Updates and Note Form */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Updates Feed - Takes 2 columns on large screens */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Project Updates</h2>
            <button
              onClick={() => {
                console.log('Refresh button clicked');
                refetchUpdates();
              }}
              disabled={updatesLoading || updatesFetching}
              className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
            >
              <RefreshCw
                size={14}
                className={updatesLoading || updatesFetching ? 'animate-spin' : ''}
              />
              {updatesLoading || updatesFetching ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          <PartnerUpdatesFeed
            updates={updates || []}
            isLoading={updatesLoading}
          />
        </div>

        {/* Note Form - Takes 1 column */}
        <div className="lg:col-span-1">
          <PartnerNoteForm projectId={projectId!} />
        </div>
      </div>
    </div>
  );
}
